/**
 * Occasion reminder lead magnet section for the public landing page (PBI #1066).
 *
 * Framing: remind yourself about someone else's occasion — do future-you a favor.
 * Occasion choices come from the system event-type catalog (same occasions we support
 * elsewhere). Double opt-in happens via email; this form only captures the request.
 *
 * Spam prevention:
 * - Hidden honeypot field (bots fill it, humans don't)
 * - Server-side rate limiting (PublicApi policy)
 * - Server-side validation and idempotent duplicate handling
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  NumberInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { FormRow } from '../common';
import { actionColors, brandColors, commonStyles, semanticColors } from '../../config/theme';
import { landingContent } from '../../content/landingContent';
import {
  getGuestOccasionOptions,
  submitGuestOccasionReminder,
  type GuestOccasionOption,
} from '../../services/publicTemplatesService';
import { dedupeOptionsByValue } from '../../utils/selectOptions';
import { isValidOccasionMonthDay, MONTH_DAY_ERROR_INVALID } from '../../utils/usDateInputParsing';

const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

interface OccasionReminderFormValues {
  personFirstName: string;
  occasionLabel: string;
  occasionMonth: string;
  occasionDay: number | '';
  email: string;
  website: string;
}

/**
 * Renders the free yearly occasion-reminder capture form on the landing page.
 */
export const OccasionReminderSection = () => {
  const copy = landingContent.sections.occasionReminder;
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [occasions, setOccasions] = useState<GuestOccasionOption[]>([]);
  const [occasionsLoading, setOccasionsLoading] = useState(true);

  const form = useForm<OccasionReminderFormValues>({
    initialValues: {
      personFirstName: '',
      occasionLabel: '',
      occasionMonth: '',
      occasionDay: '',
      email: '',
      website: '',
    },
    validate: {
      personFirstName: (value) => (value.trim() ? null : 'First name is required'),
      occasionLabel: (value) => (value.trim() ? null : 'Occasion is required'),
      occasionMonth: (value, values) => {
        if (!value) return 'Month is required';
        const day = values.occasionDay;
        if (day !== '' && day != null && !isValidOccasionMonthDay(Number(value), Number(day))) {
          return MONTH_DAY_ERROR_INVALID;
        }
        return null;
      },
      occasionDay: (value, values) => {
        if (value === '' || value == null) return 'Day is required';
        if (value < 1 || value > 31) return 'Enter a day between 1 and 31';
        const month = values.occasionMonth;
        if (!month) return null;
        if (!isValidOccasionMonthDay(Number(month), Number(value))) {
          return MONTH_DAY_ERROR_INVALID;
        }
        return null;
      },
      email: (value) => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Enter a valid email address'),
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    let cancelled = false;

    getGuestOccasionOptions()
      .then((options) => {
        if (!cancelled) {
          setOccasions(options);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOccasions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setOccasionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Option values are occasion names, which are not unique in the database.
  // Mantine Select throws on duplicate values and that throw white-screens the
  // whole public site, so dedupe before handing the list over.
  const occasionOptions = useMemo(
    () => dedupeOptionsByValue(occasions.map((o) => ({ value: o.name, label: o.name }))),
    [occasions],
  );

  const selectedOccasion = useMemo(
    () => occasions.find((o) => o.name === form.values.occasionLabel) ?? null,
    [occasions, form.values.occasionLabel],
  );

  const hasFixedCalendarDate =
    selectedOccasion?.fixedMonth != null && selectedOccasion?.fixedDay != null;

  /**
   * When the visitor picks a fixed-date holiday, lock month/day to the catalog date.
   */
  const handleOccasionChange = (value: string | null) => {
    const previous = occasions.find((o) => o.name === form.values.occasionLabel);
    const previousHadFixedDate = previous?.fixedMonth != null && previous?.fixedDay != null;

    form.setFieldValue('occasionLabel', value ?? '');

    const match = occasions.find((o) => o.name === value);
    if (match?.fixedMonth != null && match.fixedDay != null) {
      form.setFieldValue('occasionMonth', String(match.fixedMonth));
      form.setFieldValue('occasionDay', match.fixedDay);
      form.clearFieldError('occasionMonth');
      form.clearFieldError('occasionDay');
      return;
    }

    // Switching away from a fixed holiday clears a stale locked date.
    if (previousHadFixedDate) {
      form.setFieldValue('occasionMonth', '');
      form.setFieldValue('occasionDay', '');
    }
  };

  const handleSubmit = form.onSubmit(async (values) => {
    setSubmitting(true);
    setServerError(null);
    try {
      await submitGuestOccasionReminder({
        email: values.email.trim(),
        personFirstName: values.personFirstName.trim(),
        occasionLabel: values.occasionLabel.trim(),
        occasionMonth: Number(values.occasionMonth),
        occasionDay: Number(values.occasionDay),
        website: values.website,
      });
      setSubmitted(true);
    } catch {
      setServerError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Stack gap="lg">
      <Title order={2} ta="center" c={semanticColors.text.title}>
        {copy.title}
      </Title>
      <Text ta="center" c={semanticColors.text.secondary} size="md" maw={560} mx="auto">
        {copy.subtitle}
      </Text>

      <Paper
        radius="lg"
        p="xl"
        withBorder
        style={{ ...commonStyles.warmCard }}
        maw={600}
        mx="auto"
        w="100%"
      >
        {submitted ? (
          <Group gap="xs" justify="center">
            <ThemeIcon size={22} radius="xl" color={brandColors.success} variant="light">
              <IconCheck size={14} />
            </ThemeIcon>
            <Text size="sm" c="green.7">
              {copy.successMessage}
            </Text>
          </Group>
        ) : (
          <form noValidate onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label={copy.personLabel}
                placeholder={copy.personPlaceholder}
                {...form.getInputProps('personFirstName')}
              />
              <Select
                label={copy.occasionLabel}
                placeholder={copy.occasionPlaceholder}
                data={occasionOptions}
                searchable
                nothingFoundMessage={
                  occasionsLoading ? 'Loading occasions…' : 'No occasions available'
                }
                disabled={occasionsLoading || occasionOptions.length === 0}
                value={form.values.occasionLabel || null}
                onChange={handleOccasionChange}
                onBlur={() => form.validateField('occasionLabel')}
                error={form.errors.occasionLabel}
                data-path="occasionLabel"
              />
              <FormRow>
                <Select
                  label={copy.monthLabel}
                  data={MONTH_OPTIONS}
                  disabled={hasFixedCalendarDate}
                  {...form.getInputProps('occasionMonth')}
                />
                <NumberInput
                  label={copy.dayLabel}
                  min={1}
                  max={31}
                  clampBehavior="strict"
                  disabled={hasFixedCalendarDate}
                  {...form.getInputProps('occasionDay')}
                />
              </FormRow>
              <TextInput
                label={copy.emailLabel}
                placeholder={copy.emailPlaceholder}
                type="email"
                {...form.getInputProps('email')}
              />

              {/* Honeypot — hidden from users, filled by bots */}
              <Box
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                aria-hidden="true"
              >
                <TextInput
                  tabIndex={-1}
                  autoComplete="off"
                  {...form.getInputProps('website')}
                />
              </Box>

              {serverError && (
                <Text size="xs" c={brandColors.danger}>
                  {serverError}
                </Text>
              )}

              <Button type="submit" color={actionColors.save} loading={submitting}>
                {copy.submitLabel}
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Stack>
  );
};
